<script lang="ts">
  import AyaLogo from '$components/aya-logo.svelte';
  import { LL, locale } from '$i18n/i18n-svelte';
  import type { Translation, Locales } from '$i18n/i18n-types';
  import { ChevronDown, Menu, SearchIcon, X } from 'lucide-svelte';
  import ThemeSwitcher from './theme-switcher.svelte';
  import LocaleSwitcher from './locale-switcher.svelte';
  import Search from '$lib/search/search.svelte';
  import { navigating } from '$app/stores';
  import { createDialog, melt } from '@melt-ui/svelte';
  import { tick } from 'svelte';

  type TopMenu = keyof Translation['nav'];
  type MenuItem = Array<'soon'>;

  const menu: Record<TopMenu, Record<string, MenuItem>> = {
    leaderboard: {
      artisan: [],
      rp: [],
    },
    analysis: {
      characters: ['soon'],
    },
    simulator: {
      route: [],
    },
    database: {
      items: [],
      characters: [],
      route: ['soon'],
    },
  };

  const topMenuElements: HTMLLIElement[] = [];

  function topMenuKeys(): TopMenu[] {
    return Object.keys(menu) as TopMenu[];
  }

  // HACK: locale은 reactivity trigger
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getSubMenuContent(topMenu: TopMenu, key: string, locale: Locales): string {
    return ($LL.nav[topMenu] as Record<string, () => string>)[key]();
  }

  function openSubMenu(topMenu: HTMLElement) {
    topMenu.classList.add('is-open');
  }

  function handleTopMenuFocus(e: Event) {
    const element = e.currentTarget as HTMLLIElement;
    openSubMenu(element);
  }

  function handleTopMenuUnfocus(e: Event) {
    const element = e.currentTarget as HTMLLIElement;
    element.classList.remove('is-open');
  }

  function handleTopMenuClick(e: MouseEvent) {
    if (e.detail == 0) {
      e.preventDefault();

      const element = e.currentTarget as HTMLAnchorElement;
      element.parentElement?.classList.add('is-open');
    }
  }

  let isMobileMenuOpened = false;

  $: if ($navigating) {
    isMobileMenuOpened = false;
  }

  const {
    elements: {
      trigger: searchTrigger,
      overlay: searchOverlay,
      content: searchContent,
      // close: closeSearch,
      portalled: searchPortal,
    },
    states: { open: isSearchOpen },
  } = createDialog();

  async function handleWindowKeydown(e: KeyboardEvent) {
    if (e.key === '/' && !(e.currentTarget instanceof HTMLInputElement)) {
      e.preventDefault();
      isSearchOpen.set(!$isSearchOpen);
    }
  }

  isSearchOpen.subscribe(async (value) => {
    if (value) {
      await tick();
      (document.querySelector('.search-content input') as HTMLInputElement)?.focus();
    }
  });
</script>

<svelte:window on:keydown={handleWindowKeydown} />
<nav class:is-mobile-menu-open={isMobileMenuOpened}>
  <input
    class="side-menu-checkbox"
    type="checkbox"
    id="side-menu"
    bind:checked={isMobileMenuOpened}
  />
  <label for="side-menu" class="menu-trigger">
    {#if isMobileMenuOpened}
      <X size="24" />
    {:else}
      <Menu size="24" />
    {/if}
  </label>
  <a href="/{$locale}" class="home">
    <AyaLogo type="head" width="32" height="32" />
  </a>
  <ThemeSwitcher />
  <LocaleSwitcher />
  <button class="search-button" use:melt={$searchTrigger}>
    <SearchIcon size="16" />
    <span class="search-shortcut" role="none">/</span>
  </button>
  <div use:melt={$searchPortal}>
    {#if isSearchOpen}
      <div use:melt={$searchOverlay} class="search-overlay" />
      <div use:melt={$searchContent} class="search-content">
        <Search onEnter={() => ($isSearchOpen = false)} onClose={() => ($isSearchOpen = false)} />
      </div>
    {/if}
  </div>
  <!-- svelte-ignore a11y-no-noninteractive-element-to-interactive-role -->
  <menu role="menubar" class="global-menu">
    <!-- svelte-ignore a11y-mouse-events-have-key-events -->
    {#each topMenuKeys() as topMenuItem, i}
      {@const hasSubMenu = typeof menu[topMenuItem] === 'object'}
      {@const soon = Object.values(menu[topMenuItem]).every((v) => v.includes('soon'))}
      <li
        role="none"
        class="global-menu-item"
        on:mouseover={handleTopMenuFocus}
        on:mouseout={handleTopMenuUnfocus}
        bind:this={topMenuElements[i]}
      >
        <a
          role="menuitem"
          class:soon
          href={soon ? 'javascript:void(0)' : `/${$locale}/${topMenuItem}`}
          title={soon ? 'Work In Progress' : undefined}
          on:click={handleTopMenuClick}
        >
          {$LL.nav[topMenuItem].title()}
          <span class="down">
            <ChevronDown size="12" />
          </span>
        </a>
        {#if hasSubMenu}
          <menu role="menu" class="sub-menu">
            {#each Object.keys(menu[topMenuItem]) as subMenuItem}
              {@const soon = menu[topMenuItem][subMenuItem].includes('soon')}
              <li role="none">
                <a
                  role="menuitem"
                  class:soon
                  href={soon ? 'javascript:void(0)' : `/${$locale}/${topMenuItem}/${subMenuItem}`}
                  title={soon ? 'Work In Progress' : undefined}
                >
                  {getSubMenuContent(topMenuItem, subMenuItem, $locale)}
                </a>
              </li>
            {/each}
          </menu>
        {/if}
      </li>
    {/each}
  </menu>
</nav>

<style lang="postcss">
  @import '$lib/styles/breakpoints.css';

  nav {
    display: grid;
    grid-template:
      'trigger logo  search theme lang' 5rem
      'menu    menu  menu   menu  menu' 1fr
      / auto auto 1fr auto auto;
    row-gap: var(--space-3);
    column-gap: var(--space-5);
    width: 100%;
    background: var(--background);
    z-index: 2;

    &.is-mobile-menu-open {
      height: 100vh;
      position: absolute;
      top: 0;
    }

    a {
      display: block;
      text-decoration: none;
      color: var(--text);
    }

    & :global(.logo) {
      --logo-color: var(--brand);
      width: 3.2rem;
    }

    menu li {
      display: block;
      list-style: none;
    }

    padding-left: var(--space-5);
    padding-right: var(--space-5);

    @media (--md) {
      grid-template:
        'logo menu search theme lang' 5rem
        / auto 1fr auto auto auto;
      flex-direction: row;
      place-items: center;

      &.is-mobile-menu-open {
        height: 5rem;
        position: relative;
      }
    }

    :global(.theme-switcher) {
      grid-area: theme;
      align-self: center;
    }

    :global(.locale-switcher) {
      grid-area: lang;
      align-self: center;
    }
  }

  .home {
    grid-area: logo;
    align-self: center;
    width: 3.2rem;
  }

  .menu-trigger {
    grid-area: trigger;
    display: block;
    cursor: pointer;
    align-self: center;
    width: 2.4rem;

    @media (--md) {
      display: none;
    }
  }

  .down {
    display: none;

    @media (--md) {
      display: block;
    }
  }

  .side-menu-checkbox {
    display: none;
  }

  .search-button {
    display: flex;
    place-items: center;
    align-self: center;
    padding: var(--space-3) var(--space-4);

    .search-shortcut {
      display: none;
    }

    @media (--md) {
      min-width: 10rem;
      padding: var(--space-1) var(--space-2);

      .search-shortcut {
        display: block;
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border-normal);
        margin-left: auto;
        background-color: var(--background);
        border-radius: var(--radius-2);
      }
    }
  }

  .global-menu {
    grid-area: menu;
    display: none;
    flex-direction: column;
    gap: var(--space-2);
    width: 100%;
    justify-self: start;
    font-size: var(--size-8);

    @media (--md) {
      font-size: var(--size-7);
      display: flex;
      flex-direction: row;
      max-height: none;
      top: 0;
      padding-left: 0;
      position: relative;
      width: fit-content;
    }

    a {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-3) var(--space-4);
    }
  }
  .is-mobile-menu-open .global-menu {
    display: flex;
  }

  .sub-menu {
    flex-direction: column;
    padding-right: var(--space-5);
    margin-left: var(--space-5);
    border-left: 1px solid var(--mauve-6);

    a {
      padding: var(--space-3);
      padding-left: var(--space-6);
      border-radius: var(--radius-2);
      min-width: 10rem;

      &:hover {
        background-color: var(--mauve-a3);
      }

      @media (--md) {
        padding-left: var(--space-4);
      }
    }

    @media (--md) {
      display: none;
      z-index: 3;
      position: absolute;
      padding: var(--space-3);
      margin-left: 0;
      background: var(--elevation-3);
      border-radius: var(--radius-3);
      border: none;
      box-shadow: 0 0.2rem 0.4rem 0.4rem var(--shadow-1);
    }
  }
  :global(.is-open) .sub-menu {
    display: flex;
  }

  .soon::before {
    content: '🚧';
  }

  .search-overlay {
    position: fixed;
    inset: 0;
    background-color: var(--black-a9);
    z-index: 50;
  }
  .search-content {
    position: fixed;
    z-index: 50;
    left: 0;
    top: 0;

    @media (--sm) {
      left: 50%;
      top: 10rem;
      /* max-height: 80vh;
      width: 50vw; */
      transform: translate(-50%, -50%);
    }
  }
</style>
